import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { format, parse } from 'date-fns'
import * as moment from 'moment'
import { IndividualConfig, ToastrService } from 'ngx-toastr'

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  constructor(private router: Router, private toastr: ToastrService) {}

  createToasterConfig(): Partial<IndividualConfig> {
    return {
      enableHtml: true,
      progressBar: true,
      progressAnimation: 'decreasing',
      tapToDismiss: false,
      closeButton: true
    }
  }

  routeTo = (routingURL: string) => {
    this.router.navigateByUrl(routingURL)
  }

  routeWithQueryParams = (routingURL: any, queryParams: Array<any>) => {
    this.router.navigate([routingURL, ...queryParams])
  }

  route = (...url: string[]) => {
    this.router.navigateByUrl(this.createRoutingURL(url))
  }

  routeWithPathParams(routingURL: string, pathParams: string[] = []) {
    this.router.navigate(['/', routingURL, ...pathParams])
  }

  createRoutingURL = (url: string[]): string => {
    return url.reduce((acc, segment) => `${acc}/${segment}`)
  }

  isDate = (date: any): boolean => {
    const parsedDate = Date.parse(date)
    return (isNaN(date) && !isNaN(parsedDate)) || Object.prototype.toString.call(date) === '[object Date]'
  }

  isNumber = (date: any): boolean => {
    return Object.prototype.toString.call(date) === '[object Number]'
  }

  isString = (date: any): boolean => {
    return Object.prototype.toString.call(date) === '[object String]'
  }

  isOfSameType = (input1: any, input2: any): boolean => {
    return Object.prototype.toString.call(input1) === Object.prototype.toString.call(input2)
  }

  createShowMessage =
    (type: string) =>
    (msg: string, title: string = 'SOP', enableHtml: boolean = false) => {
      const toasterConfig = this.createToasterConfig()
      this.toastr[type](msg, title, toasterConfig)
    }

  showSuccess = this.createShowMessage('success')

  showError = this.createShowMessage('error')

  showWarning = this.createShowMessage('warning')

  showInfo = this.createShowMessage('info')

  findObj = (list: any[], keyToCheckInObj: string, valueToMatch: string) => {
    return list.find((obj) => obj[keyToCheckInObj] === valueToMatch)
  }

  extractApiErrorMessage(err: unknown): string | undefined {
    if (!err) {
      return undefined
    }

    if (typeof err === 'string') {
      try {
        const parsed = JSON.parse(err)
        if (parsed?.status === 429) {
          return parsed?.error
        }
        return parsed?.error?.message
      } catch {
        return undefined
      }
    }

    if ((err as any)?.error?.message) {
      return (err as any).error.message
    }

    return undefined
  }
}

export const formatDate = (date: Date | string, inputFormat: string, outputFormat: string): string => {
  if (!date) return ''
  const parsedDate = typeof date === 'string' ? parse(date, inputFormat, new Date()) : date
  return format(parsedDate, outputFormat)
}

export const formatDateByFormat = (dateString: string, format: string) => {
  return moment(dateString).format(format)
}

export const formatDateToDDMMYYYY = (dateString: string) => {
  if (!dateString) {
    return ''
  }
  const options = { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions
  const [datePart] = dateString.split('T')
  const date = new Date(datePart)
  return date.toLocaleDateString('en-GB', options).replace(/\//g, '/')
}

export const getNextWorkingDay = () => {
  const nextDay = moment().add(24, 'hours')

  if (nextDay.isoWeekday() >= 6) {
    nextDay.add(1, 'week').startOf('isoWeek')
  }

  nextDay.set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
  return nextDay.toISOString()
}

export const addDaysToUIFormattedDate = (days: number) => {
  const currentDate = new Date()
  currentDate.setDate(currentDate.getDate() + days)

  const year = currentDate.getFullYear()
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
  const day = currentDate.getDate().toString().padStart(2, '0')

  return `${day}/${month}/${year}`
}

export const generateOptionsMapFromSet = (set) => {
  const resultMap = new Map()
  set.forEach((value) => {
    resultMap.set(value, value)
  })
  return resultMap
}

export const isIntelTenant = (hostname: string = window?.location?.hostname): boolean => {
  try {
    return hostname?.toLowerCase()?.includes('intel') ?? false
  } catch {
    return false
  }
}
export const isADITenant = (hostname: string = window?.location?.hostname): boolean => {
  try {
    return hostname?.toLowerCase()?.includes('adi') ?? false
  } catch {
    return false
  }
}
